import * as THREE from "three";
import { GLTFExporter } from "three/examples/jsm/exporters/GLTFExporter";
import { mergeGeometries } from "three/examples/jsm/utils/BufferGeometryUtils";

/**
 * Re-exporting the viewer scene instead of shipping the bucket GLB.
 *
 * The file in the bucket is the raw photogrammetry mesh: it has neither the
 * orientation/scale the user set in the viewer (those live on the Three.js
 * nodes, see orientationUpdated/scalingUpdated) nor the PV arrays (separate
 * objects parented to the scene, not to the model). To ship both in one file
 * we rebuild an export root from the live scene and run GLTFExporter over it.
 *
 * PV arrays are drawn with InstancedMesh2 (@three.ez/instanced-mesh), whose
 * per-instance matrices live in a GPU texture — `instanceMatrix` is a
 * zero-length placeholder, so neither GLTFExporter's EXT_mesh_gpu_instancing
 * plugin nor OBJExporter can see the instances. We read the matrices back with
 * getMatrixAt() and merge them into one plain geometry per mesh, which is also
 * the most portable thing to hand a CAD/Blender user.
 */

// Yield to the event loop so the progress bar repaints during long merges.
const PANELS_PER_TICK = 250;
const nextTick = () => new Promise((resolve) => setTimeout(resolve, 0));

function throwIfAborted(signal) {
  if (signal?.aborted) {
    throw new DOMException("Export cancelled", "AbortError");
  }
}

function areaPanelMeshes(area) {
  return [
    area?.instancedMesh,
    area?.ewWestMesh,
    area?.verticalPanelInstancedMesh,
    area?.horizontalPanelInstancedMesh,
  ].filter((mesh) => mesh && mesh.visible);
}

function areaLegacyPanels(area) {
  return (area?.panels ?? []).map((panel) => panel?.plane).filter((plane) => plane?.visible);
}

function visibleInstanceCount(mesh) {
  const count = mesh.instancesCount ?? 0;
  if (!mesh.getVisibilityAt) return count;
  let visible = 0;
  for (let i = 0; i < count; i++) if (mesh.getVisibilityAt(i)) visible += 1;
  return visible;
}

/** True when there is at least one PV panel in the scene worth exporting. */
export function hasExportablePanels(areas = []) {
  return areas.some((area) => {
    if (area?.show === false) return false;
    if (areaLegacyPanels(area).length > 0) return true;
    return areaPanelMeshes(area).some((mesh) => visibleInstanceCount(mesh) > 0);
  });
}

/**
 * Total panel count — used only to spread progress across the merge. Counts
 * visible instances only, to match what the merge actually walks.
 */
function countPanels(areas) {
  return areas.reduce((total, area) => {
    const instanced = areaPanelMeshes(area).reduce(
      (sum, mesh) => sum + visibleInstanceCount(mesh),
      0
    );
    return total + instanced + areaLegacyPanels(area).length;
  }, 0);
}

/**
 * Bake a texture's UV transform and color space into its pixels, so the
 * exporters only ever see a plain sRGB image with an identity transform.
 *
 * Panel textures are set up with `center=(0.5,0.5)`, `rotation=±π/2` and
 * `flipY=false` for the portrait variants (loadPanelTextures), and plain
 * `flipY=true` for the landscape ones. A rotation on the texture matrix has no
 * equivalent in an MTL `map_Kd`, and glTF only expresses it through
 * KHR_texture_transform, which most importers ignore — so the panels would come
 * out with their cells running the wrong way. Redraw the image instead and hand
 * the exporters a transform-free texture.
 *
 * Derivation of the target image, with `setUvTransform` for rotation π/2 about
 * (0.5, 0.5) giving the sampled coordinate `(u', v') = (v, 1 - u)`: the baked
 * image must satisfy `I'(x, y) = I(y, h - x)`, i.e. the source rotated a quarter
 * turn clockwise on a canvas with swapped dimensions. `flipY: true` means the
 * upload is flipped, so it resolves to a plain vertical flip. Both are emitted
 * as `flipY: false`, matching the glTF atlas convention the OBJ writer already
 * uses for the model.
 *
 * `offset`/`repeat` are left alone — panels never set them, and folding a
 * repeat into the image would need a tiled redraw. See `encodeCanvasToSRGB` for
 * the color-space half.
 */
function bakeTextureTransform(texture, cache) {
  const image = texture?.image;
  const quarterTurns = Math.round((texture?.rotation || 0) / (Math.PI / 2)) & 3;
  const needsEncode = texture && texture.colorSpace !== THREE.SRGBColorSpace;
  if (!image || (quarterTurns === 0 && texture.flipY !== true && !needsEncode)) {
    return texture;
  }
  if (cache.has(texture.uuid)) return cache.get(texture.uuid);

  const w = image.width;
  const h = image.height;
  const swap = quarterTurns % 2 === 1;
  const canvas = document.createElement("canvas");
  canvas.width = swap ? h : w;
  canvas.height = swap ? w : h;

  const ctx = canvas.getContext("2d");
  ctx.translate(canvas.width / 2, canvas.height / 2);
  // Canvas y grows downward, so a positive UV rotation is a clockwise one here.
  if (quarterTurns) ctx.rotate(quarterTurns * (Math.PI / 2));
  if (texture.flipY === true) ctx.scale(1, -1);
  ctx.drawImage(image, -w / 2, -h / 2);
  if (needsEncode) encodeCanvasToSRGB(ctx, canvas);

  const baked = new THREE.CanvasTexture(canvas);
  baked.flipY = false;
  baked.colorSpace = THREE.SRGBColorSpace;
  baked.wrapS = texture.wrapS;
  baked.wrapT = texture.wrapT;
  baked.name = texture.name;
  cache.set(texture.uuid, baked);
  return baked;
}

const SRGB_LUT = (() => {
  const lut = new Uint8Array(256);
  for (let i = 0; i < 256; i++) {
    const c = i / 255;
    const encoded = c <= 0.0031308 ? c * 12.92 : 1.055 * Math.pow(c, 1 / 2.4) - 0.055;
    lut[i] = Math.round(encoded * 255);
  }
  return lut;
})();

/**
 * Re-encode a canvas from linear-light to sRGB.
 *
 * Panel textures are loaded without a `colorSpace`, so three treats their pixels
 * as linear-light and skips the sRGB→linear decode; with the renderer's sRGB
 * output that means the viewer shows `linearToSRGB(pixel)` — noticeably brighter
 * than the file on disk. glTF and MTL base-color images are sRGB by definition,
 * so a correct importer decodes them and the panels would come out much darker
 * than the user approved. Applying the viewer's output encode here cancels that
 * decode out, so the export reproduces what the viewer shows.
 *
 * The condition on this is `colorSpace !== SRGBColorSpace`: tagging the panel
 * textures properly at load time (which is arguably the real fix, at the cost of
 * changing how the viewer itself looks) turns this into a no-op.
 */
function encodeCanvasToSRGB(ctx, canvas) {
  const data = ctx.getImageData(0, 0, canvas.width, canvas.height);
  const px = data.data;
  for (let i = 0; i < px.length; i += 4) {
    px[i] = SRGB_LUT[px[i]];
    px[i + 1] = SRGB_LUT[px[i + 1]];
    px[i + 2] = SRGB_LUT[px[i + 2]]; // alpha left alone
  }
  ctx.putImageData(data, 0, 0);
}

/**
 * Panels are exported fully opaque. The viewer draws them at partial alpha (0.5
 * for instanced panels, the area's `transparencyLevel` for legacy ones) so the
 * roof reads through them while planning, but a deliverable wants solid panels —
 * and blending would otherwise double-darken wherever panels overlap.
 *
 * `depthTest: false` is kept for the exported root only. In the viewer itself
 * panels are depth-tested again and select mode moves them into the annotation
 * overlay pass instead (setRenderingPriority); an export has no such pass, so
 * the flag here is what keeps the baked panels on top. Neither glTF 2.0 nor MTL
 * has a depth-state or draw-order field, so this rides along on the Three.js
 * objects only — it holds if this root is ever rendered in-app, but no importer
 * reads it back out of the GLB or OBJ.
 */
function exportMaterial(source, textureCache) {
  const material = source.clone();
  material.transparent = false;
  material.depthTest = false;
  if (material.map) material.map = bakeTextureTransform(material.map, textureCache);
  return material;
}

/**
 * Split a panel geometry into its textured faces and its black frame edges.
 *
 * The viewer paints the four thin side faces of the panel box black with vertex
 * colors and lets `map * vertexColor` do the rest (paintPanelEdgesBlack). OBJ
 * has no vertex colors at all, and COLOR_0 in glTF is widely ignored on import,
 * so both would show the panel image smeared along the edges. Express the same
 * thing with geometry instead: triangles whose vertices are all black become a
 * separate untextured black material.
 *
 * The two halves differ only in their index and keep the full (24-vertex) vertex
 * buffer, so the split costs one extra copy of the base panel geometry, not of
 * every instance.
 */
function splitPanelEdges(base) {
  const color = base.getAttribute("color");
  const index = base.getIndex();
  if (!color || !index) return { faces: base, edges: null };

  const isBlack = (v) => color.getX(v) === 0 && color.getY(v) === 0 && color.getZ(v) === 0;
  const faceIndices = [];
  const edgeIndices = [];
  for (let i = 0; i < index.count; i += 3) {
    const tri = [index.getX(i), index.getX(i + 1), index.getX(i + 2)];
    (tri.every(isBlack) ? edgeIndices : faceIndices).push(...tri);
  }
  if (edgeIndices.length === 0 || faceIndices.length === 0) {
    return { faces: base, edges: null };
  }

  const withIndex = (indices) => {
    const geometry = base.clone();
    geometry.deleteAttribute("color");
    geometry.setIndex(indices);
    return geometry;
  };
  return { faces: withIndex(faceIndices), edges: withIndex(edgeIndices) };
}

// Instance a base geometry across the visible instance matrices and merge.
async function mergeInstances(base, mesh, { signal, onPanel }) {
  const matrix = new THREE.Matrix4();
  const chunks = [];
  const count = mesh.instancesCount ?? 0;
  for (let i = 0; i < count; i++) {
    if (i % PANELS_PER_TICK === 0) {
      throwIfAborted(signal);
      await nextTick();
    }
    // Deleted panels stay in the buffer with visibility off (same check the
    // PV report uses to count panels).
    if (mesh.getVisibilityAt && !mesh.getVisibilityAt(i)) continue;
    mesh.getMatrixAt(i, matrix);
    chunks.push(base.clone().applyMatrix4(matrix));
    if (onPanel) onPanel();
  }
  if (chunks.length === 0) return null;
  const merged = mergeGeometries(chunks, false);
  chunks.forEach((geometry) => geometry.dispose());
  if (!merged) return null;
  mesh.updateWorldMatrix(true, false);
  return merged.applyMatrix4(mesh.matrixWorld);
}

/**
 * Bake one InstancedMesh2 into world-space meshes — one for the panel faces and,
 * when the geometry carries the black-edge vertex colors, one for the edges.
 */
async function bakeInstancedMesh(mesh, { signal, onPanel, textureCache } = {}) {
  if ((mesh.instancesCount ?? 0) === 0) return [];

  // The library injects a GL-only `instanceIndex` attribute into the geometry
  // it renders; GLTFExporter cannot serialise a GLBufferAttribute, and its
  // clone() returns the same object, so strip it from our copy.
  const source = mesh.geometry.clone();
  source.deleteAttribute("instanceIndex");
  const { faces, edges } = splitPanelEdges(source);

  const out = [];
  // Only the face pass reports progress — the edge pass walks the same
  // instances and would double-count.
  const facesGeometry = await mergeInstances(faces, mesh, { signal, onPanel });
  if (facesGeometry) {
    const material = exportMaterial(mesh.material, textureCache);
    material.name = material.name || "pv_panel";
    out.push(new THREE.Mesh(facesGeometry, material));
  }
  if (edges) {
    const edgesGeometry = await mergeInstances(edges, mesh, { signal });
    if (edgesGeometry) {
      out.push(new THREE.Mesh(edgesGeometry, panelEdgeMaterial()));
    }
    edges.dispose();
  }
  if (faces !== source) faces.dispose();
  source.dispose();
  return out;
}

// The frame stands in for the black vertex colors; opaque like the faces.
function panelEdgeMaterial() {
  const material = new THREE.MeshBasicMaterial({ color: 0x000000, depthTest: false });
  material.name = "pv_panel_edge";
  return material;
}

/** Bake the legacy per-panel meshes (older projects keep real Mesh objects). */
function bakeLegacyPanels(planes, textureCache) {
  const byMaterial = new Map();
  planes.forEach((plane) => {
    plane.updateWorldMatrix(true, false);
    const geometry = plane.geometry.clone().applyMatrix4(plane.matrixWorld);
    const key = plane.material.uuid;
    if (!byMaterial.has(key)) byMaterial.set(key, { material: plane.material, chunks: [] });
    byMaterial.get(key).chunks.push(geometry);
  });

  const meshes = [];
  byMaterial.forEach(({ material, chunks }) => {
    const merged = chunks.length === 1 ? chunks[0] : mergeGeometries(chunks, false);
    if (chunks.length > 1) chunks.forEach((geometry) => geometry.dispose());
    if (!merged) return;
    meshes.push(new THREE.Mesh(merged, exportMaterial(material, textureCache)));
  });
  return meshes;
}

/**
 * Build the object that gets exported: the model with its viewer transform,
 * plus (optionally) the PV arrays baked into world-space meshes.
 *
 * The model is cloned (geometry and materials are shared, so this is cheap)
 * and its world matrix is flattened onto the clone, so the group can sit at
 * the identity next to the world-space panels.
 *
 * Call `dispose()` on the result when done to free the merged panel geometry.
 */
export async function buildExportRoot({
  modelObject,
  areas = [],
  includeArrays = false,
  onProgress,
  signal,
} = {}) {
  throwIfAborted(signal);

  const root = new THREE.Group();
  root.name = "airteam_export";

  modelObject.updateWorldMatrix(true, false);
  const modelClone = modelObject.clone(true);
  modelClone.matrix.copy(modelObject.matrixWorld);
  modelClone.matrix.decompose(modelClone.position, modelClone.quaternion, modelClone.scale);
  modelClone.matrixAutoUpdate = false;
  modelClone.name = modelObject.name || "model";
  root.add(modelClone);

  const created = [];
  // Panel types are shared across areas, so bake each texture's UV transform
  // at most once.
  const textureCache = new Map();
  if (includeArrays) {
    const visibleAreas = areas.filter((area) => area?.show !== false);
    const total = countPanels(visibleAreas) || 1;
    let done = 0;
    const onPanel = () => {
      done += 1;
      if (onProgress && done % PANELS_PER_TICK === 0) onProgress(done / total);
    };

    for (const area of visibleAreas) {
      for (const mesh of areaPanelMeshes(area)) {
        const baked = await bakeInstancedMesh(mesh, { signal, onPanel, textureCache });
        created.push(...baked);
      }
      const legacy = areaLegacyPanels(area);
      if (legacy.length > 0) {
        created.push(...bakeLegacyPanels(legacy, textureCache));
        done += legacy.length;
      }
      throwIfAborted(signal);
    }
    created.forEach((mesh, i) => {
      // Unique names: OBJ writes one `o <name>` block per mesh and importers
      // may collapse blocks that share a name.
      mesh.name = `pv_panels_${i + 1}`;
      root.add(mesh);
    });
    if (onProgress) onProgress(1);
  }

  // OBJExporter transforms vertices through mesh.matrixWorld, and this root
  // never enters the scene graph — without this the model would be written
  // without its orientation/scale while the (already world-space) panels stay
  // put, leaving the two far apart.
  root.updateMatrixWorld(true);

  root.dispose = () => {
    created.forEach((mesh) => {
      mesh.geometry.dispose();
      mesh.material.dispose();
    });
    // Cloned materials share the live scene's textures except for the baked
    // canvases, which only exist for this export.
    textureCache.forEach((texture) => texture.dispose());
  };
  return root;
}

/** Material uuids used by an object — lets the OBJ writer tell model from panels. */
export function collectMaterialUuids(object3D) {
  const uuids = new Set();
  object3D.traverse((child) => {
    if (!child.isMesh || !child.material) return;
    const list = Array.isArray(child.material) ? child.material : [child.material];
    list.forEach((material) => uuids.add(material.uuid));
  });
  return uuids;
}

/**
 * Serialise an Object3D to a GLB blob.
 *
 * Note on texture size: GLTFExporter re-encodes every texture through a canvas
 * and defaults to PNG, which would turn a JPEG photogrammetry atlas into a
 * file many times larger. It honours `texture.userData.mimeType`, which
 * GLTFLoader populates from the source file, so a loaded model round-trips as
 * JPEG. Panel textures are PNGs with alpha and stay PNG.
 */
export async function exportGlbBlob(object3D, { signal } = {}) {
  throwIfAborted(signal);
  const buffer = await new GLTFExporter().parseAsync(object3D, {
    binary: true,
    onlyVisible: true,
  });
  throwIfAborted(signal);
  return new Blob([buffer], { type: "model/gltf-binary" });
}
